'use client';

import * as Dialog from '@radix-ui/react-dialog';
import type { ComponentProps, ReactNode } from 'react';

import { DialogOverlay } from '@eliadmin/core/src/components/dlz/dialogs/dialog-overlay';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Centered form dialog of the product pages – Figma „Napísať hodnotenie“ 60152:14529: white card,
 * radius 18, 24 px padding and gap, DLZ/Heading/H3 title over a body-M description, close icon at the
 * top right, the shared 30 % overlay. Rendered inside the `<Dialog open>` root of the core dialog
 * store. The close button is last in the DOM so the first field takes the initial focus.
 */
export function DialogCard(props: DialogCardProps) {
  return (
    <Dialog.Portal>
      <DialogOverlay />
      <DialogCardContent {...props} />
    </Dialog.Portal>
  );
}

type DialogCardProps = {
  title: ReactNode;
  /** Subtitle under the title (the product name); without it the dialog has no description. */
  description?: ReactNode;
  children: ReactNode;
} & Omit<ComponentProps<typeof Dialog.Content>, 'title' | 'children'>;

/**
 * The card without the portal and the overlay, for dialogs that swap between several cards under
 * one overlay (the authentication dialog).
 */
export function DialogCardContent({
  title,
  description,
  className,
  children,
  ...props
}: DialogCardProps) {
  const { t } = useTranslations();

  return (
    <Dialog.Content
      className={cn(
        'fixed left-1/2 top-1/2 z-[200] flex max-h-[calc(100dvh-32px)] w-[calc(100%-32px)] max-w-[652px] -translate-x-1/2 -translate-y-1/2 flex-col gap-6 overflow-y-auto rounded-[18px] bg-white p-4 shadow-[0_8px_24px_-4px_rgba(15,16,18,0.08),0_2px_4px_rgba(15,16,18,0.06)] focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 motion-reduce:animate-none sm:p-6',
        className,
      )}
      {...(description ? {} : { 'aria-describedby': undefined })}
      {...props}
    >
      <header className="flex flex-col gap-2 pr-10">
        <Dialog.Title className="dlz-h3 text-[#0F1012]">{title}</Dialog.Title>
        {description ? (
          <Dialog.Description className="dlz-body-m text-[#6B7079]">
            {description}
          </Dialog.Description>
        ) : null}
      </header>
      {children}
      <Dialog.Close
        aria-label={t('text272')}
        className="absolute right-4 top-4 rounded-full p-1 text-[#9096A0] transition-colors hover:text-[#0F1012] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] sm:right-6 sm:top-6"
      >
        <Icon name="x" size={24} />
      </Dialog.Close>
    </Dialog.Content>
  );
}
